import { type CarFromLicensePlateRecordWithOwners, VehicleCategory } from '@navio/nsdk';
import { revalidateLogic } from '@tanstack/react-form';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorMessageInternal, NoddiSheet, useAppForm } from 'noddi-ui-web';
import { VEHICLE_CATEGORY_OPTIONS } from 'noddi-util';
import { useEffect } from 'react';
import { z } from 'zod';
import { VehicleCatalogMatchPicker } from './VehicleCatalogMatchPicker';

const FORM_ID = 'edit-car-details-form';
// Stored on the signature link and shown in its match history: the match moves every car with this
// registry signature, so the next person can see who changed it and why.
const EVIDENCE_TOOLTIP = 'Kept in the match history. The match applies to every car with the same registry signature.';
const schema = z.object({
  catalogNode: z.object({ value: z.number(), label: z.string() }).optional(),
  evidence: z.string(),
  vehicleCategory: z.enum(VehicleCategory)
});
type Props = { open: boolean; onClose: () => void; car: CarFromLicensePlateRecordWithOwners };

/**
 * The one editor for a car's catalog match and vehicle class, in every backoffice app: the match is
 * shared by every car with the same registry signature, so the evidence rule has to be the same too.
 */

export const EditCarDetailsSheet = ({ open, onClose, car }: Props) => {
  const { mutateAsync, error } = noddiAsync.usePatch({
    type: URLKeys.patchCarDetails,
    queryConfig: {
      meta: { suppressErrorToast: true },
      onSuccess: () => invalidateQueryExactMatch({ urlKey: URLKeys.getCar, input: { carId: car.id } })
    }
  });
  const defaults: z.input<typeof schema> = {
    catalogNode: undefined,
    evidence: '',
    vehicleCategory: car.vehicleCategory
  };
  const form = useAppForm({
    defaultValues: defaults,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: schema },
    onSubmit: async ({ value }) => {
      await mutateAsync(
        {
          carId: car.id,
          vehicleCategory: value.vehicleCategory,
          ...(value.catalogNode ? { catalogNodeId: value.catalogNode.value, catalogMatchEvidence: value.evidence } : {})
        },
        { onSuccess: onClose }
      );
    }
  });
  useEffect(() => {
    if (open) {
      form.reset({ catalogNode: undefined, evidence: '', vehicleCategory: car.vehicleCategory });
    }
  }, [open, car.vehicleCategory, form]);
  return (
    <form.AppForm>
      <NoddiSheet
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            onClose();
          }
        }}
        title='Edit car information'
        description='A catalog match applies to every car with the same registry signature. Pick the make and model to narrow the catalog, then the generation. Leave it empty to keep the current match.'
        footer={<form.SubscribeButton formId={FORM_ID} label='Save changes' />}
      >
        {error && (
          <div role='alert'>
            <ApiErrorMessageInternal error={error} />
          </div>
        )}
        <form
          id={FORM_ID}
          className='flex flex-col gap-5'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.AppField name='catalogNode'>
            {(field) => <VehicleCatalogMatchPicker car={car} onChange={field.handleChange} />}
          </form.AppField>
          <form.AppField name='evidence'>
            {(field) => (
              <field.Input
                label='Why this match? (optional)'
                placeholder='e.g. VIN decode, registry papers, photo of the trim badge'
                tooltip={EVIDENCE_TOOLTIP}
              />
            )}
          </form.AppField>
          <form.AppField name='vehicleCategory'>
            {(field) => <field.Select label='Vehicle category' options={VEHICLE_CATEGORY_OPTIONS} />}
          </form.AppField>
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
